import { type JSX, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { ImportModal } from '@/components/admin/import-modal';
import { RateFormModal } from '@/components/admin/rate-form-modal';
import {
  Badge,
  Button,
  Card,
  ConfirmModal,
  IconAction,
  Skeleton,
} from '@/components/ui';
import {
  usePreviewImportRates,
  useRunImportRates,
} from '@/hooks/useAdminImports';
import { useRateCards } from '@/hooks/useAdminRateCards';
import {
  useAllRates,
  useDeleteRate,
  useMoveRate,
  useUpsertRate,
  type Rate,
  type RateWithCard,
} from '@/hooks/useAdminRates';
import { useAuth } from '@/hooks/useAuth';
import { ApiError } from '@/lib/api';
import { can } from '@/lib/permissions';
import { formatCurrency, formatRelativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Reseller admin → Rates (global view across every card the reseller owns).
// Lives at /admin/rates. Differs from /admin/rate-cards/:cardId in that it
// flattens rates regardless of card and lets the operator move/edit/delete
// from one screen — useful when reorganizing pricing tiers without
// bouncing through each card detail page. Import goes through a small
// "pick a destination card" step before the standard ImportModal kicks
// in (the importer is per-card on the wire).
// ---------------------------------------------------------------------------
export default function AdminRatesAllPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  const { user } = useAuth();
  const canEdit = can.useAdminModule(user?.role);
  // Every card in this view belongs to the calling tenant, so ownership
  // is uniform: a reseller session never sees a platform-owned card here.
  const derivedCarrier = user?.tenantType !== 'platform';
  const list = useAllRates();
  const cards = useRateCards();

  const [editing, setEditing] = useState<RateWithCard | null>(null);
  const [pendingDelete, setPendingDelete] = useState<RateWithCard | null>(null);
  const [importTargetCardId, setImportTargetCardId] = useState<number | null>(null);
  const [showImportPicker, setShowImportPicker] = useState(false);
  // "New rate" from this cross-card view needs to know which card the
  // new row lands in. We pick the card via a small chooser, then mount
  // the standard RateFormModal scoped to the chosen cardId.
  const [newRateTargetCardId, setNewRateTargetCardId] = useState<number | null>(null);
  const [showNewRatePicker, setShowNewRatePicker] = useState(false);

  const items = useMemo(() => list.data?.items ?? [], [list.data?.items]);
  const cardOptions = useMemo(() => cards.data?.items ?? [], [cards.data?.items]);

  const distinctCards = useMemo(() => {
    const ids = new Set(items.map((r) => r.rate_card_id));
    return ids.size;
  }, [items]);

  return (
    <div className="px-8 py-8 max-w-[1100px] mx-auto">
      <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-none m-0">
            {t('arall_title')}
          </h1>
          <p className="text-[13px] text-text-3 mt-1.5">
            {items.length === 0
              ? t('arall_sub_empty')
              : t('arall_sub', {
                  total: items.length.toLocaleString(lang),
                  cards: distinctCards,
                })}
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Link to="/admin/rate-cards">
              <Button variant="secondary">{t('arall_manage_cards')}</Button>
            </Link>
            <Button
              variant="secondary"
              onClick={() => setShowImportPicker(true)}
              disabled={cardOptions.length === 0}
              title={cardOptions.length === 0 ? t('arall_import_no_cards') : undefined}
            >
              {t('arall_import')}
            </Button>
            <Button
              onClick={() => setShowNewRatePicker(true)}
              disabled={cardOptions.length === 0}
              title={cardOptions.length === 0 ? t('arall_import_no_cards') : undefined}
            >
              <Icon.plus width="14" height="14" />
              {t('arall_new')}
            </Button>
          </div>
        )}
      </header>

      <Card padding="none">
        {list.isLoading ? (
          <div className="p-5 space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-[14px] text-text-2 font-medium">{t('arall_empty_title')}</p>
            <p className="text-[13px] text-text-3 mt-1 max-w-[460px] mx-auto">
              {t('arall_empty_sub')}
            </p>
            {canEdit && (
              <Link to="/admin/rate-cards" className="inline-block mt-5">
                <Button>
                  <Icon.plus width="14" height="14" />
                  {t('arall_empty_cta')}
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                  <th className="px-5 py-2.5 font-medium">{t('arall_col_card')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('arall_col_prefix')}</th>
                  {!derivedCarrier && (
                    <th className="px-5 py-2.5 font-medium">{t('arall_col_carrier')}</th>
                  )}
                  <th className="px-5 py-2.5 font-medium">{t('arall_col_msg_type')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('arall_col_buy')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('arall_col_sell')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('arall_col_updated')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('arall_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <RateRow
                    key={r.id}
                    rate={r}
                    lang={lang}
                    cardOptions={cardOptions}
                    onEdit={() => setEditing(r)}
                    onDelete={() => setPendingDelete(r)}
                    canEdit={canEdit}
                    derivedCarrier={derivedCarrier}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <EditRateGate
          rate={editing}
          derivedCarrier={derivedCarrier}
          onClose={() => setEditing(null)}
        />
      )}

      <DeleteRateGate
        pending={pendingDelete}
        derivedCarrier={derivedCarrier}
        onClose={() => setPendingDelete(null)}
      />

      {showImportPicker && (
        <CardPickerModal
          title={t('arall_import_pick_title')}
          subtitle={t('arall_import_pick_sub')}
          continueLabel={t('arall_import_pick_continue')}
          cards={cardOptions}
          onCancel={() => setShowImportPicker(false)}
          onPick={(cardId) => {
            setImportTargetCardId(cardId);
            setShowImportPicker(false);
          }}
        />
      )}

      {importTargetCardId !== null && (
        <ImportRatesGate
          cardId={importTargetCardId}
          cardName={cardOptions.find((c) => c.id === importTargetCardId)?.name ?? ''}
          onClose={() => setImportTargetCardId(null)}
        />
      )}

      {showNewRatePicker && (
        <CardPickerModal
          title={t('arall_new_pick_title')}
          subtitle={t('arall_new_pick_sub')}
          continueLabel={t('arall_new_pick_continue')}
          cards={cardOptions}
          onCancel={() => setShowNewRatePicker(false)}
          onPick={(cardId) => {
            setNewRateTargetCardId(cardId);
            setShowNewRatePicker(false);
          }}
        />
      )}

      {newRateTargetCardId !== null && (
        <CreateRateGate
          cardId={newRateTargetCardId}
          derivedCarrier={derivedCarrier}
          onClose={() => setNewRateTargetCardId(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------
interface RateRowProps {
  rate: RateWithCard;
  lang: string;
  cardOptions: { id: number; name: string; is_default: boolean }[];
  onEdit: () => void;
  onDelete: () => void;
  canEdit: boolean;
  derivedCarrier: boolean;
}
function RateRow({
  rate,
  lang,
  cardOptions,
  onEdit,
  onDelete,
  canEdit,
  derivedCarrier,
}: RateRowProps): JSX.Element {
  const { t } = useTranslation('default');
  const move = useMoveRate();
  const [error, setError] = useState<string | null>(null);

  const handleMove = async (e: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
    const targetCardId = Number(e.target.value);
    if (!targetCardId || targetCardId === rate.rate_card_id) return;
    setError(null);
    try {
      await move.mutateAsync({ rateId: rate.id, targetCardId });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('arall_err_move'));
    }
  };

  return (
    <tr className="border-b border-line last:border-0 hover:bg-surface-2">
      <td className="px-5 py-3 align-top">
        <select
          value={rate.rate_card_id}
          onChange={(e) => void handleMove(e)}
          disabled={move.isPending || cardOptions.length <= 1 || !canEdit}
          className="h-8 px-2 text-[12.5px] rounded-md bg-surface border border-line text-text outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow disabled:opacity-60"
        >
          {cardOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.is_default ? ` (${t('arc_default_pill')})` : ''}
            </option>
          ))}
        </select>
        {error && <div className="text-[11px] text-danger mt-1">{error}</div>}
      </td>
      <td className="px-5 py-3 align-top">
        <div>
          <span className="mono font-medium text-text">{rate.prefix === '*' ? '* (any)' : rate.prefix}</span>
          {rate.name && <div className="text-[11px] text-text-3 mt-0.5">{rate.name}</div>}
        </div>
      </td>
      {!derivedCarrier && (
        <td className="px-5 py-3 align-top text-text">{rate.provider_name}</td>
      )}
      <td className="px-5 py-3 align-top">
        {rate.message_type ? (
          <Badge variant="neutral">{t(`ar_msg_${rate.message_type}`)}</Badge>
        ) : (
          <span className="text-[11px] text-text-4">{t('ar_msg_any')}</span>
        )}
      </td>
      <td className={cn('px-5 py-3 align-top num text-right text-text-2')}>
        {formatCurrency(rate.buy_cost, lang)}
      </td>
      <td className="px-5 py-3 align-top num text-right text-text font-medium">
        {formatCurrency(rate.sell_price, lang)}
      </td>
      <td className="px-5 py-3 align-top text-text-3 text-[12px] whitespace-nowrap">
        {formatRelativeTime(rate.updated_at, lang)}
      </td>
      <td className="px-5 py-3 align-top text-right">
        {canEdit ? (
          <div className="inline-flex gap-0.5">
            <IconAction icon={Icon.pencil} label={t('ar_edit')} onClick={onEdit} />
            <IconAction icon={Icon.trash} label={t('ar_delete')} onClick={onDelete} tone="danger" />
          </div>
        ) : (
          <span className="text-text-4 text-[12px]">—</span>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Edit gate — useUpsertRate is parameterized by cardId, so we wrap it in
// a tiny gate that only mounts (and binds the hook) once we have a rate
// to edit. That way we don't have to lift the cardId up to a context.
// ---------------------------------------------------------------------------
function EditRateGate({
  rate,
  onClose,
  derivedCarrier,
}: {
  rate: RateWithCard;
  onClose: () => void;
  derivedCarrier: boolean;
}): JSX.Element {
  const upsert = useUpsertRate(rate.rate_card_id);
  const editingRate: Rate = rate;
  return (
    <RateFormModal
      editing={editingRate}
      derivedCarrier={derivedCarrier}
      onClose={onClose}
      submitting={upsert.isPending}
      onSubmit={async (input) => {
        await upsert.mutateAsync(input);
        onClose();
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Delete gate — same pattern. useDeleteRate also wants the cardId at
// hook construction; the gate scopes it to the rate being deleted.
// ---------------------------------------------------------------------------
function DeleteRateGate({
  pending,
  onClose,
  derivedCarrier,
}: {
  pending: RateWithCard | null;
  onClose: () => void;
  derivedCarrier: boolean;
}): JSX.Element {
  const { t } = useTranslation('default');
  // Hooks must be called unconditionally — fall back to 0 when there's
  // no pending rate; the mutation never runs because the modal is gated
  // on `pending !== null` below.
  const cardId = pending?.rate_card_id ?? 0;
  const remove = useDeleteRate(cardId);

  const handleConfirm = async (): Promise<void> => {
    if (!pending) return;
    try {
      await remove.mutateAsync(pending.id);
    } finally {
      onClose();
    }
  };

  return (
    <ConfirmModal
      open={pending !== null}
      title={t('ar_delete_title')}
      description={
        pending
          ? derivedCarrier
            ? t('ar_delete_desc_reseller', { prefix: pending.prefix })
            : t('ar_delete_desc', {
                prefix: pending.prefix,
                carrier: pending.provider_name,
              })
          : ''
      }
      confirmLabel={t('ar_delete_action')}
      cancelLabel={t('ar_delete_cancel')}
      tone="danger"
      busy={remove.isPending}
      onConfirm={() => void handleConfirm()}
      onClose={onClose}
    />
  );
}

// ---------------------------------------------------------------------------
// Card picker — reusable two-step entry point. Used both by Import and
// "New rate" (which need to know which card the rows/row land in before
// opening the corresponding modal). Localized titles/labels are passed
// in so the same component serves both flows.
// ---------------------------------------------------------------------------
interface CardPickerModalProps {
  title: string;
  subtitle: string;
  continueLabel: string;
  cards: { id: number; name: string; is_default: boolean }[];
  onCancel: () => void;
  onPick: (cardId: number) => void;
}
function CardPickerModal({
  title,
  subtitle,
  continueLabel,
  cards,
  onCancel,
  onPick,
}: CardPickerModalProps): JSX.Element {
  const { t } = useTranslation('default');
  const defaultCard = cards.find((c) => c.is_default);
  const [selected, setSelected] = useState<number>(defaultCard?.id ?? cards[0]?.id ?? 0);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
    >
      <div
        className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[420px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-line">
          <h2 className="display text-[16px] font-semibold text-text">{title}</h2>
          <p className="text-[12px] text-text-3 mt-0.5">{subtitle}</p>
        </div>
        <div className="px-5 py-4 space-y-3">
          <label className="block text-[12px] font-medium text-text-2 mb-1.5">
            {t('arall_import_pick_label')}
          </label>
          <select
            value={selected}
            onChange={(e) => setSelected(Number(e.target.value))}
            className="w-full h-9 px-3 text-[13px] rounded-md bg-surface border border-line text-text outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow"
          >
            {cards.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.is_default ? ` (${t('arc_default_pill')})` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="px-5 py-4 border-t border-line flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            {t('arall_import_pick_cancel')}
          </Button>
          <Button onClick={() => onPick(selected)} disabled={!selected}>
            {continueLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create-rate gate — useUpsertRate is parameterized by cardId, so we
// mount the form modal scoped to the card the operator picked. Symmetric
// with EditRateGate above.
// ---------------------------------------------------------------------------
function CreateRateGate({
  cardId,
  onClose,
  derivedCarrier,
}: {
  cardId: number;
  onClose: () => void;
  derivedCarrier: boolean;
}): JSX.Element {
  const upsert = useUpsertRate(cardId);
  return (
    <RateFormModal
      editing={null}
      derivedCarrier={derivedCarrier}
      onClose={onClose}
      submitting={upsert.isPending}
      onSubmit={async (input) => {
        await upsert.mutateAsync(input);
        onClose();
      }}
    />
  );
}

function ImportRatesGate({
  cardId,
  cardName,
  onClose,
}: {
  cardId: number;
  cardName: string;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation('default');
  const previewImport = usePreviewImportRates(cardId);
  const runImport = useRunImportRates(cardId);

  return (
    <ImportModal
      title={t('arall_import_title', { card: cardName })}
      subtitle={t('arall_import_sub', { card: cardName })}
      schemaHint={t('ar_import_schema_hint')}
      onClose={onClose}
      onPreview={(file) => previewImport.mutateAsync(file)}
      onImport={(file) => runImport.mutateAsync(file)}
    />
  );
}
